import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import GovernanceContinuum from './GovernanceContinuum';
import ApexGlyph from './ApexGlyph';

const ROWS = [
  { label: 'Control State',     before: 'Compliant ✓',                after: '⚠ Gap detected',           afterOk: false },
  { label: 'Policy Version',    before: 'v2.3 · Current ✓',           after: 'v2.1 ⚠ Modified 2h prior', afterOk: false },
  { label: 'Data Sources',      before: 'All approved ✓',             after: '⚠ Unlisted source active', afterOk: false },
  { label: 'Evidence Coverage', before: '94% ✓',                      after: '71% ⚠ Below threshold',    afterOk: false },
  { label: 'Oversight',         before: 'Mandatory · Met ✓',          after: '⚠ Below defensibility',    afterOk: false },
  { label: 'Proof Grade',       before: '◆ AUDIT-GRADE',              after: '◆ OPERATIONS-GRADE',       afterOk: true  },
];

const MONO = 'JetBrains Mono, monospace';
const TRANSITION_MS = 600;
const SOUND_KEY = 'apex_replay_sound';

/**
 * Synthesizes a brief Apex whoosh — sub-1KB, runtime-generated via Web Audio API.
 * Direction-aware: 'degrade' sweeps high→low (collapse), 'recover' sweeps low→high.
 * Filtered noise + frequency sweep, gain-ramped to avoid clicks.
 */
function playWhoosh(audioCtxRef, direction = 'degrade') {
  try {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;
    const dur = 0.85;

    // Filtered noise body — emulates air-passing
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const noise = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i += 1) {
      noise[i] = (Math.random() * 2 - 1) * 0.55;
    }
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = noiseBuffer;

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.Q.value = 0.9;
    // Sweep the bandpass frequency for the whoosh contour.
    if (direction === 'degrade') {
      noiseFilter.frequency.setValueAtTime(1800, now);
      noiseFilter.frequency.exponentialRampToValueAtTime(260, now + dur);
    } else {
      noiseFilter.frequency.setValueAtTime(260, now);
      noiseFilter.frequency.exponentialRampToValueAtTime(1800, now + dur);
    }

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.18, now + 0.12);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    // Subtle sine companion for body / pitch sensation.
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    if (direction === 'degrade') {
      osc.frequency.setValueAtTime(360, now);
      osc.frequency.exponentialRampToValueAtTime(90, now + dur);
    } else {
      osc.frequency.setValueAtTime(90, now);
      osc.frequency.exponentialRampToValueAtTime(360, now + dur);
    }
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.0001, now);
    oscGain.gain.exponentialRampToValueAtTime(0.08, now + 0.18);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    // Master gain — keeps the whoosh polite (peak ~ -16 dBFS).
    const master = ctx.createGain();
    master.gain.value = 0.55;

    noiseSrc.connect(noiseFilter).connect(noiseGain).connect(master);
    osc.connect(oscGain).connect(master);
    master.connect(ctx.destination);

    noiseSrc.start(now);
    osc.start(now);
    noiseSrc.stop(now + dur + 0.05);
    osc.stop(now + dur + 0.05);
  } catch (_) { /* swallow — audio is decorative */ }
}

export default function GovernanceReplay() {
  // `severity` is the single source of truth — 0..1.
  // Buttons animate it to 0 or 1. The scrub slider sets it directly.
  // The row values flip at severity >= 0.5.
  const [severity, setSeverity] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const isAfter = severity >= 0.5;

  // Time scrub: severity 0 → "Apr 14, 2026 · 23:59:59 UTC", severity 1 → "Apr 15, 2026 · 11:14:32 UTC".
  // 11h 14m 33s span = 40,473 seconds.
  const SCRUB_START_MS = Date.UTC(2026, 3, 14, 23, 59, 59); // Apr 14, 23:59:59 UTC
  const SCRUB_SPAN_SEC = 40473;
  function timestampAt(sev) {
    const ms = SCRUB_START_MS + Math.round(sev * SCRUB_SPAN_SEC) * 1000;
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
    return `${month} ${d.getUTCDate()}, ${d.getUTCFullYear()} · ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
  }
  const tsLabel =
    severity < 0.03 ? `CURRENT · ${timestampAt(0)}` :
    severity > 0.97 ? `RECONSTRUCTED · ${timestampAt(1)}` :
    `SCRUBBING · ${timestampAt(severity)}`;

  // Sound toggle — opt-in, persisted to localStorage. Default OFF (muted).
  const [soundOn, setSoundOn] = useState(false);
  useEffect(() => {
    try {
      setSoundOn(localStorage.getItem(SOUND_KEY) === 'on');
    } catch (_) { /* ignore */ }
  }, []);
  function toggleSound() {
    setSoundOn((v) => {
      const next = !v;
      try { localStorage.setItem(SOUND_KEY, next ? 'on' : 'off'); } catch (_) { /* ignore */ }
      return next;
    });
  }
  const audioCtxRef = useRef(null);

  // Animate severity from current value to target over `ms` for button clicks.
  const animRafRef = useRef(0);
  function animateSeverityTo(target, ms = TRANSITION_MS) {
    cancelAnimationFrame(animRafRef.current);
    const start = severity;
    const startT = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startT) / ms);
      // ease-in-out cubic
      const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      setSeverity(start + (target - start) * eased);
      if (t < 1) animRafRef.current = requestAnimationFrame(tick);
    };
    animRafRef.current = requestAnimationFrame(tick);
  }

  function handleSetActive(target) {
    // target: 'before' | 'after'
    const sev = target === 'after' ? 1 : 0;
    if ((sev === 1 && severity >= 0.999) || (sev === 0 && severity <= 0.001)) return;
    if (transitioning) return;
    setTransitioning(true);
    if (soundOn) {
      setTimeout(() => playWhoosh(audioCtxRef, target === 'after' ? 'degrade' : 'recover'), 200);
    }
    // Schedule the foreground scan/value-swap window — keeps the existing 600ms feel.
    setTimeout(() => {
      animateSeverityTo(sev, 700);
      setTransitioning(false);
    }, TRANSITION_MS);
  }

  // Scrub track interactions — pointer events on the track give us drag + click for free.
  const trackRef = useRef(null);
  function setSeverityFromPointer(clientX) {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setSeverity(ratio);
  }
  function onScrubPointerDown(e) {
    if (transitioning) return;
    e.preventDefault();
    cancelAnimationFrame(animRafRef.current);
    setScrubbing(true);
    setSeverityFromPointer(e.clientX);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }
  function onScrubPointerMove(e) {
    if (!scrubbing) return;
    setSeverityFromPointer(e.clientX);
  }
  function onScrubPointerUp(e) {
    if (!scrubbing) return;
    setScrubbing(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
  }

  return (
    <div
      data-testid="governance-replay"
      style={{
        position: 'relative',
        background: '#0F1629', border: '1px solid #1E2A45',
        borderRadius: 20, padding: 32, maxWidth: 900, margin: '0 auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        transition: scrubbing ? 'none' : 'border-color 0.4s ease, box-shadow 0.4s ease',
        borderColor: severity >= 0.5
          ? `rgba(245,158,11,${0.15 + severity * 0.25})`
          : `rgba(16,185,129,${0.15 + (1 - severity) * 0.15})`,
        overflow: 'hidden',
      }}
    >
      {/* Continuum backdrop — degrades as the replay state degrades */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          borderRadius: 20,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: 0,
          transition: 'opacity 0.4s ease',
        }}
        data-testid="replay-continuum-backdrop"
      >
        <GovernanceContinuum
          height={260}
          opacity={0.06 + severity * 0.10}
          severity={severity}
          showLabels={false}
        />
      </div>

      {/* Foreground content — sits above the Continuum backdrop */}
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MONO, fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em' }}>
          Governance Replay · Legal Document Drafting
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
            47,832 decisions · single workflow
          </span>
          <button
            type="button"
            onClick={toggleSound}
            data-testid="replay-sound-toggle"
            aria-label={soundOn ? 'Mute replay sound' : 'Enable replay sound'}
            aria-pressed={soundOn}
            title={soundOn ? 'Mute replay sound' : 'Enable replay sound'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24,
              background: 'transparent',
              border: `1px solid ${soundOn ? 'rgba(13,148,136,0.5)' : '#1E2A45'}`,
              borderRadius: 6,
              color: soundOn ? '#0D9488' : '#475569',
              cursor: 'pointer',
              transition: 'color 0.2s ease, border-color 0.2s ease',
            }}
          >
            {soundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
          </button>
        </div>
      </div>

      {transitioning && (
        <div
          aria-hidden
          data-testid="replay-transitioning-glyph"
          style={{
            position: 'absolute', top: 16, right: 16,
            opacity: 0.4,
            animation: 'apex-replay-glyph-pulse 0.6s ease-in-out infinite',
            zIndex: 2,
          }}
        >
          <ApexGlyph size={16} />
        </div>
      )}

      {/* Toggle + scrub track */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 12, flexWrap: 'nowrap' }}>
        <button
          onClick={() => handleSetActive('before')}
          data-testid="replay-before"
          disabled={transitioning}
          style={{
            fontSize: 12, fontFamily: MONO, padding: '8px 18px', borderRadius: 8,
            background: severity <= 0.02 ? '#10B981' : 'transparent',
            border: '1px solid',
            borderColor: severity <= 0.02 ? '#10B981' : '#1E2A45',
            color: severity <= 0.02 ? 'white' : '#475569',
            cursor: transitioning ? 'wait' : 'pointer',
            transition: 'all 0.3s ease',
            letterSpacing: '0.04em',
            fontWeight: 600,
            opacity: transitioning ? 0.7 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Apr 14, 2026 ◆
        </button>

        {/* Scrub track — pointer-driven, severity = relative-X. */}
        <div
          ref={trackRef}
          data-testid="replay-scrub-track"
          role="slider"
          aria-label="Scrub through governance state"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={Number(severity.toFixed(2))}
          tabIndex={0}
          onPointerDown={onScrubPointerDown}
          onPointerMove={onScrubPointerMove}
          onPointerUp={onScrubPointerUp}
          onPointerCancel={onScrubPointerUp}
          onKeyDown={(e) => {
            if (transitioning) return;
            if (e.key === 'ArrowLeft')  { e.preventDefault(); setSeverity((s) => Math.max(0, s - 0.05)); }
            if (e.key === 'ArrowRight') { e.preventDefault(); setSeverity((s) => Math.min(1, s + 0.05)); }
            if (e.key === 'Home')        { e.preventDefault(); setSeverity(0); }
            if (e.key === 'End')         { e.preventDefault(); setSeverity(1); }
          }}
          style={{
            position: 'relative',
            flex: '1 1 auto',
            minWidth: 120,
            maxWidth: 360,
            height: 24,
            cursor: transitioning ? 'wait' : (scrubbing ? 'grabbing' : 'grab'),
            display: 'flex',
            alignItems: 'center',
            touchAction: 'none',
            outline: 'none',
          }}
        >
          {/* Track baseline — slate, with a colored fill from teal (clean) to amber (degraded). */}
          <div aria-hidden style={{
            position: 'absolute', left: 0, right: 0, top: '50%',
            height: 2, marginTop: -1,
            background: '#1E2A45', borderRadius: 1,
          }} />
          <div aria-hidden style={{
            position: 'absolute', left: 0, top: '50%',
            width: `${severity * 100}%`,
            height: 2, marginTop: -1,
            background: `linear-gradient(90deg, #10B981 0%, ${severity > 0.5 ? '#F59E0B' : '#10B981'} 100%)`,
            borderRadius: 1,
            transition: scrubbing ? 'none' : 'width 0.2s ease',
          }} />
          {/* Midpoint tick — marks the value-flip threshold */}
          <div aria-hidden style={{
            position: 'absolute', left: '50%', top: '50%',
            width: 1, height: 6, marginTop: -3, marginLeft: -0.5,
            background: '#334155',
          }} />
          {/* Thumb */}
          <div aria-hidden style={{
            position: 'absolute',
            left: `calc(${severity * 100}% - 6px)`,
            top: '50%', marginTop: -6,
            width: 12, height: 12, borderRadius: '50%',
            background: severity > 0.5 ? '#F59E0B' : '#10B981',
            border: `2px solid ${severity > 0.5 ? '#F59E0B' : '#10B981'}`,
            boxShadow: scrubbing
              ? `0 0 16px ${severity > 0.5 ? '#F59E0B' : '#10B981'}`
              : `0 0 8px ${severity > 0.5 ? '#F59E0B88' : '#10B98188'}`,
            transition: scrubbing ? 'none' : 'left 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
          }} />
        </div>

        <button
          onClick={() => handleSetActive('after')}
          data-testid="replay-after"
          disabled={transitioning}
          style={{
            fontSize: 12, fontFamily: MONO, padding: '8px 18px', borderRadius: 8,
            background: severity >= 0.98 ? '#F59E0B' : 'transparent',
            border: '1px solid',
            borderColor: severity >= 0.98 ? '#F59E0B' : '#1E2A45',
            color: severity >= 0.98 ? '#0A0A0F' : '#475569',
            cursor: transitioning ? 'wait' : 'pointer',
            transition: 'all 0.3s ease',
            letterSpacing: '0.04em',
            fontWeight: 600,
            opacity: transitioning ? 0.7 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Apr 15, 2026 ⚠
        </button>
      </div>

      {/* Helper + Precision timestamp — now driven live from severity */}
      <div style={{ textAlign: 'center', marginBottom: 24, marginTop: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.16em' }}>
          ← drag to reconstruct governance state →
        </span>
        <br />
        <span
          data-testid="replay-timestamp"
          style={{
            fontFamily: MONO, fontSize: 8,
            color: severity > 0.5 ? `rgba(245,158,11,${0.25 + severity * 0.4})` : `rgba(148,163,184,${0.20 + (1 - severity) * 0.3})`,
            letterSpacing: '0.12em', marginTop: 4, display: 'inline-block',
            transition: scrubbing ? 'none' : 'color 0.4s ease',
          }}
        >
          {tsLabel}
        </span>
      </div>

      {/* State rows */}
      <div data-testid="replay-rows">
        {ROWS.map((row) => {
          const value = isAfter ? row.after : row.before;
          const okState = !isAfter || row.afterOk;
          return (
            <div
              key={row.label}
              data-testid={`replay-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                borderTop: '1px solid #1E2A45', padding: '14px 0', minHeight: 40,
              }}
            >
              <span style={{ fontFamily: MONO, fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {row.label}
              </span>
              {transitioning ? (
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    width: 160, height: 11,
                    background: 'linear-gradient(90deg, #1E2A45, #2D3F5C, #1E2A45)',
                    backgroundSize: '200% 100%',
                    animation: 'apex-replay-scan 0.6s linear infinite',
                    borderRadius: 2,
                  }}
                />
              ) : (
                <span
                  key={`${row.label}-${isAfter ? 'after' : 'before'}`}
                  className="apex-replay-value"
                  style={{
                    fontFamily: MONO, fontSize: 11,
                    color: okState ? '#10B981' : '#F59E0B',
                    textAlign: 'right',
                  }}
                >
                  {value}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Impact row */}
      <div
        data-testid="replay-impact"
        style={{
          marginTop: 18, padding: 14, borderRadius: 10,
          background: isAfter ? 'rgba(245,158,11,0.06)' : 'rgba(16,185,129,0.04)',
          border: `1px solid ${isAfter ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.15)'}`,
          transition: 'all 0.4s ease',
          opacity: transitioning ? 0.4 : 1,
        }}
      >
        <span style={{
          fontFamily: MONO, fontSize: 11,
          color: isAfter ? '#F59E0B' : '#10B981',
          transition: 'color 0.4s ease',
          letterSpacing: '0.04em',
        }}>
          {isAfter
            ? '⚠ 143 decisions affected · 2 evidence packages invalidated · Proof grade degraded'
            : '◆ All 47,832 decisions in this window: proof chain intact'}
        </span>
      </div>

      {isAfter && (
        <p
          data-testid="replay-degradation-line"
          style={{
            fontFamily: MONO,
            fontSize: 9,
            color: '#F59E0B',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            textAlign: 'center',
            marginTop: 14,
            marginBottom: 0,
            opacity: Math.min(1, (severity - 0.5) * 4),
            transition: 'opacity 0.3s ease',
          }}
        >
          Control State degraded at decision time. 143 decisions affected.
        </p>
      )}

      {/* Footer caption */}
      <p style={{
        marginTop: 20, fontFamily: MONO, fontSize: 9,
        color: '#334155', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: 0,
      }}>
        This is not a dashboard. This is governance state reconstruction.
      </p>
      </div>{/* /foreground */}

      <style>{`
        @keyframes apex-replay-scan {
          0%   { background-position:  200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes apex-replay-glyph-pulse {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50%      { opacity: 0.65; transform: scale(1.15); }
        }
        @keyframes apex-replay-value-in {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .apex-replay-value {
          display: inline-block;
          animation: apex-replay-value-in 0.35s ease-out forwards;
          will-change: opacity, transform;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="governance-replay"] .apex-replay-value { animation: none !important; }
          [data-testid="governance-replay"] [aria-hidden] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
